import { Anchor, Button, Stack, Text, Title } from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { getCurrentUser } from "../shared/getCurrentUser";
import { NavBarContainer } from "./NavBarContainer";

function buildAppLoginQuery(params: URLSearchParams): string {
  const redirectUri = params.get("redirect_uri") || "";
  const state = params.get("state") || "";
  const codeChallenge = params.get("code_challenge") || "";
  const parts = [
    "redirect_uri=" + encodeURIComponent(redirectUri),
    "code_challenge=" + encodeURIComponent(codeChallenge),
  ];
  if (state) {
    parts.push("state=" + encodeURIComponent(state));
  }
  return parts.join("&");
}

/**
 * Confirm desktop app sign-in when the browser already has a session.
 * Continue hits the server mint endpoint; switch account clears the cookie then re-enters /applogin.
 */
export function AppLoginPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const user = getCurrentUser();
  const query = buildAppLoginQuery(params);
  const hasRequiredParams =
    !!params.get("redirect_uri") && !!params.get("code_challenge");

  React.useEffect(() => {
    if (!hasRequiredParams) {
      return;
    }
    if (!user) {
      // Let the MVC /applogin entry start Google OAuth with the same query.
      window.location.href = "/applogin?" + query;
    }
  }, [user, hasRequiredParams, query]);

  if (!hasRequiredParams) {
    return (
      <NavBarContainer>
        <Stack gap="md" maw={480}>
          <Title order={3}>{t("applogin.invalidTitle")}</Title>
          <Text>{t("applogin.invalidBody")}</Text>
        </Stack>
      </NavBarContainer>
    );
  }

  if (!user) {
    return (
      <NavBarContainer>
        <Stack gap="md" maw={480}>
          <Text>{t("applogin.redirecting")}</Text>
        </Stack>
      </NavBarContainer>
    );
  }

  const email = user.AccountEmail || user.Email || "";
  const continueUrl = "/applogin?" + query + "&confirm=1";
  const switchAccountUrl =
    "/logout?return=" + encodeURIComponent("/applogin?" + query);

  return (
    <NavBarContainer>
      <Stack gap="md" maw={480}>
        <Title order={3}>{t("applogin.title")}</Title>
        <Text>{t("applogin.signedInAs", { email })}</Text>
        <Text>{t("applogin.intro")}</Text>
        <Button component="a" href={continueUrl}>
          {t("applogin.continue")}
        </Button>
        <Anchor href={switchAccountUrl}>{t("applogin.switchAccount")}</Anchor>
      </Stack>
    </NavBarContainer>
  );
}
